import { trackElementClicked } from "@/lib/analytics/events";
import { flushMarketingEventOutboxNow } from "@/lib/analytics/marketingEventOutbox";
import { parseAnalyticsElementId, type AnalyticsElementId } from "@/lib/analytics/analyticsElementId";

type ResolveOptions = {
  /**
   * Start a best-effort keepalive outbox drain right after the click is queued.
   * Set by links whose navigation may unload the document before the poller runs.
   */
  flushOutboxOnClick?: boolean;
};

type ResolvedAnalyticsElement = {
  /** DOM marker attributes (`data-analytics-element` / `data-analytics-surface`). */
  attributes: {
    "data-analytics-element": AnalyticsElementId;
    "data-analytics-surface": string;
  };
  /** Queues `fe_element_clicked` for this element. */
  emitClick: () => void;
};

/**
 * Resolves the optional `analyticsElementId` prop shared by the interactive components
 * (`Button`, `AdminButton`, `ExternalLink`, `InternalLink`, …) into the marker attributes
 * and click emitter. A malformed id resolves to undefined (with a local warning), so the
 * component renders untracked instead of breaking.
 */
export function resolveAnalyticsElement(
  elementId: AnalyticsElementId | undefined,
  { flushOutboxOnClick = false }: ResolveOptions = {},
): ResolvedAnalyticsElement | undefined {
  if (!elementId) return undefined;

  const parsed = parseAnalyticsElementId(elementId);
  if (!parsed) return undefined;

  return {
    attributes: {
      "data-analytics-element": parsed.elementId,
      "data-analytics-surface": parsed.surface,
    },
    emitClick: () => {
      trackElementClicked({ elementId });
      if (flushOutboxOnClick) {
        flushMarketingEventOutboxNow();
      }
    },
  };
}
